import React from 'react';
import {getStudentReadingLevel} from './readerCharacteristics';
import {getRandomInt} from '../library';
import {logSessionInfo} from './logger';
import {COPY_STRINGS} from 'Constants/translatedCopy';

import {
  interventionType,
  scopeType,
  rules,
} from 'Constants/interventionSelectionConstants';
import {
  rhymes,
  stopWords,
  spanishStopWords,
} from 'Constants/interventionStrings';
import { getUserData } from './API';
import { getCopyByGradeband } from './ThemingService';

// File-scoped counters
let storyInterventions = 0;
let phraseInterventions = 0;
let lastPhraseIntervention = false;
let wordInterventionsThisStory = 0;
let wordInterventionsThisPhrase = 0;
let wordInterventionsInWindow = 0;
let storyGambits = 0;
let pageGambits = 0;
let sentenceGambits = 0;
let interventionHistory = [];
let phraseHistory = [];
let errorsInLastPhrase = 0;
let readWithMe = false;
let phraseInterventionOccurranceHistory = [];
let skipIndices = [];

//Check whether we can intervene on a word
export function wordShouldIntervene(endOfPhrase = false) {
  if (((wordInterventionsThisStory < rules.maxWordInterventionsInStory) &&
      (wordInterventionsInWindow < rules.maxWordInterventionsInWindow) ||
      (endOfPhrase)) &&
      (wordInterventionsThisPhrase < rules.maxWordInterventionsInPhrase)) {
    return true;
  } else {
    return false;
  }
}

export function isStopWord(word) {
  let inWord = cleanWord(word);
  return ((getUserData().locale === 'en-us') ? (stopWords[getStudentReadingLevel()].indexOf(inWord) >= 0) : (spanishStopWords.indexOf(inWord) >= 0));
}

export function alreadyIntervened(inWord) {
  return phraseHistory.some(intervention => intervention.word == inWord);
}

export function hasRhyme(inWord){
  return rhymes.some(rhyme => rhyme.word == inWord.toLowerCase());
}

export function getRhyme(inWord){
  if(!hasRhyme(inWord)) return null;

  let result = rhymes.filter(rhyme => {
    return rhyme.word === inWord.toLowerCase();
  })[0].rhyme;
  return result;
}

export function wordDidIntervene(intervention,endOfPhrase=false) {
  interventionHistory.push(intervention);
  endOfPhrase ? logSessionInfo("chose_phrase_intervention",intervention) : logSessionInfo("chose_word_intervention",intervention);

  phraseHistory.push(intervention);
  if(!endOfPhrase){
    wordInterventionsThisPhrase++;
  }

  if(intervention.type === interventionType.skip){
    skipIndices.push(intervention.index);
  }
  // TODO double check whether this counter is being incremented on phrase level interventions
  // of type 'WORD'
  if(intervention.type !== interventionType.skip && intervention.type !== interventionType.none){
    // console.log('word push 1');
    phraseInterventionOccurranceHistory.push(1);
    wordInterventionsInWindow += 2;
    wordInterventionsThisStory++;
  }
}

//Take care of counters on phrase end
export function onPhraseInterventionSelected(intervention) {
  lastPhraseIntervention = false;
  // TODO refactor so this check is unnecessary
  // (currently, both this check and wordDidIntervene are called
  // under some conditions)
  if (intervention.scope == scopeType.phrase) {
    if(interventionType !== interventionType.none) {
      // console.log('phrase push 1');
      phraseInterventionOccurranceHistory.push(1);
    }
    interventionHistory.push(intervention);
    logSessionInfo("chose_phrase_intervention",intervention);
    if (intervention.type == interventionType.read) lastPhraseIntervention = true;
  }
  phraseHistory = [];
  wordInterventionsThisPhrase = 0; //Always reset the sentence level
  skipIndices = [];
  if (wordInterventionsInWindow > 0) wordInterventionsInWindow = wordInterventionsInWindow - 1;
}

// called before each activity
export function resetInterventionHistory() {
  storyInterventions = 0;
  phraseInterventions = 0;
  lastPhraseIntervention = false;
  wordInterventionsThisStory = 0;
  wordInterventionsThisPhrase = 0;
  phraseInterventionOccurranceHistory = [];
  wordInterventionsInWindow = 0;
  storyGambits = 0;
  pageGambits = 0;
  sentenceGambits = 0;
  interventionHistory = [];
  phraseHistory = [];
  errorsInLastPhrase = 0;
  readWithMe = false;
}

export function getPageIntervention(wpm, accuracy) {
  if ((pageShouldIntervene() == false)) return null;

  pageGambits++; //increment our page gambit counter

  if (accuracy >= rules.accuracyForGoodGambits) {
    const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'pageGambitGoodPhrases');
    return (contentArray[getRandomInt(0, contentArray.length - 1)]);
  } else {
    const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'pageGambitStrugglingPhrases');
    return (contentArray[getRandomInt(0, contentArray.length - 1)]);
  }

}

// This section contains accessors for strings used in the interventions
// These belong here because the things we say to the student are interventions in and of themselves
function cleanWord(inWord) {
  // Return '' for nullish/empty input so isStopWord doesn't throw on .replace when handed
  // an undefined word — e.g. an out-of-range phrase index on a skip-back. '' is never a
  // stop word (indexOf -1). Callers still pass strings; this only guards the empty case.
  if (!inWord) return '';
  return (inWord.replace(/[.,\/#!$%\^&\*;:{}=\-_`'~()""?!]/g, "").toLowerCase());
}

export function pageShouldIntervene() {
  // TODO: We are temporarily (or maybe permanently) disabling the page gambits. Leave the original code in until
  // a final decision is made.
  //return ((pageGambits < rules.maxPageGambitsPerStory) && (Math.random() <= (rules.pageInterventionFrequency)));
  return false;
}

export function getStoryIntervention(wpm, accuracy, inReadingLevel) {
  const contentObject = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'storyPhrases');
  let textToSpeak = contentObject[getStudentReadingLevel()][getRandomInt(0, contentObject[getStudentReadingLevel()].length)];
  if(wpm && accuracy){
    textToSpeak += ` You red this page at ${wpm} words per minute with ${accuracy}% accuracy.`;
  }
  return (textToSpeak);
}

export function getTooFastPhrases(){
  let textToSpeak = COPY_STRINGS.intervention_strings.toofastPhrases[getStudentReadingLevel()][getRandomInt(0, COPY_STRINGS.intervention_strings.toofastPhrases[getStudentReadingLevel()].length)];
  return (textToSpeak);
}
export function getKeepReading(inReadingLevel) {
  // console.log("inReadingLevel: ", inReadingLevel);
  let level = inReadingLevel;
  if(level != 'proficient' && level != 'capable' && level != 'beginner'){
    level = 'beginner';
  }
  let textToSpeak = "";
  const contentObject = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'keep_reading');
  if(contentObject[level]){
    textToSpeak = contentObject[level][getRandomInt(0, contentObject[level].length)];
  }
  return (textToSpeak);
}

export function getLastPhraseOnPageResponse(){
  const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'lastPhraseOnPageResponse');
  let textToSpeak = contentArray[getRandomInt(0, contentArray.length)];
  return (textToSpeak);
}

export function getReadWithMePrompt(){
  const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'readWithMePrompt');
  let textToSpeak = contentArray[getRandomInt(0, contentArray.length)];
  return (textToSpeak);
}

export function getTryAgain() {
  const contentArray = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'try_again');
  let textToSpeak = contentArray[getRandomInt(0, contentArray.length)];
  return (textToSpeak);
}

export function getEndOfClap(inReadingLevel){
	const endOfClapPhrasesContainer = COPY_STRINGS.intervention_strings.endOfClapPhrases;
	const endOfClapPhrases = getCopyByGradeband(endOfClapPhrasesContainer, inReadingLevel);
  let textToSpeak = endOfClapPhrases[getRandomInt(0, endOfClapPhrases.length)];
  return (textToSpeak);
}

export function getComprehensionIntro(){
  const contentObject = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'comprehensionIntro');
  let textToSpeak = contentObject[getStudentReadingLevel()][getRandomInt(0, contentObject[getStudentReadingLevel()].length)];
  return (textToSpeak);
}

export function getComprehensionRightAnswer(){
  const contentObject = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'comprehensionRightAnswer');
  let textToSpeak = contentObject[getStudentReadingLevel()][getRandomInt(0, contentObject[getStudentReadingLevel()].length)];
  return (textToSpeak);
}

export function getComprehensionRight(){
  const contentObject = getCopyByGradeband(COPY_STRINGS.intervention_strings, 'comprehensionRight');
  let textToSpeak = contentObject[getStudentReadingLevel()][getRandomInt(0, contentObject[getStudentReadingLevel()].length)];
  return (textToSpeak)
}

export function speakInterventionLeadIn(speech, inType,inWord="this sentence", onEnd=null){
  let outString = "";

  if((inType == interventionType.rhyme) || (inType == interventionType.clap) || (inType == interventionType.simpledefinition)){
    outString = COPY_STRINGS.intervention_strings.interventionOneErrorLeadInDontSayWord[getStudentReadingLevel()][getRandomInt(0, COPY_STRINGS.intervention_strings.interventionOneErrorLeadInDontSayWord[getStudentReadingLevel()].length)] + " ";
  }else if(inType == interventionType.spellout){
      outString = COPY_STRINGS.intervention_strings.spellOutLeadIn;
  }else if(inType == interventionType.spelloutBefore) {
    outString = COPY_STRINGS.intervention_strings.spellOutLeadInBeforeVersion;
  }else{
    if(inType === interventionType.read || inType === interventionType.repeatsentence){
      outString = COPY_STRINGS.intervention_strings.interventionMultipleErrorLeadIn[getStudentReadingLevel()][getRandomInt(0, COPY_STRINGS.intervention_strings.interventionMultipleErrorLeadIn[getStudentReadingLevel()].length)] + " ";
    }else if(inType == interventionType.spellout){
      outString = COPY_STRINGS.intervention_strings.spellOutLeadIn;
    }else{
      outString = COPY_STRINGS.intervention_strings.interventionOneErrorLeadInSayWord[getStudentReadingLevel()][getRandomInt(0, COPY_STRINGS.intervention_strings.interventionOneErrorLeadInSayWord[getStudentReadingLevel()].length)] + " ";
    }
  }

  //TODO: this is hacky, and would break if we were to add more than 1 param to these strings. The goal is to deprecate the ${p0} string replacement
  //  model in favor of the new way of doing things (a function returned from the copy store which we evaluate on mount rather than on build.)
  //  if we add new intervention strings in the future, instead of using this format we should take the opportunity to adopt the new strategy.
  //  eventually when we have deprecated all ${p0} strings, this code should be removed
  if(Array.isArray(outString)){
    if(getUserData().bilingualMode){
      for(let i = 0; i < outString.length; i++){
        if(i == 0){
          speech.speak(outString[i]);
        } else {
          speech.speak(outString[i], undefined, undefined, true);
        }
      }
    } else {
      let textToSpeak = "";
      for(let i = 0; i < outString.length; i++){
        textToSpeak += outString[i].textToSpeak + " ";
      }
      speech.speak({textToSpeak: textToSpeak});
    }
  } else if(outString.includes("${p0}")){
    //NOTE: for now this code assumes that inWord will not be the first word in the text to speak
    // Check if in bilingual mode
    if(getUserData().bilingualMode){
      //TODO: add onEnd functionality to this case too. not necessary short term because only intervention that uses onEnd is clap, and clap doesn't use ${p0} text
      let speechArray = outString.split("${p0}");
      speech.speak({textToSpeak: speechArray[0].trim()});
      speech.speak({textToSpeak: inWord, skipTranslation: true}, undefined, undefined, true);
      for(let i = 1; i < speechArray.length; i++){
        if(speechArray[i].replace(/[^a-zA-Z]/g, "").length > 0){ //make sure we're not speaking any strings which are punctuation only
          speech.speak({textToSpeak: speechArray[i].trim()});
        }
      }
    } else {
      speech.speak({textToSpeak: outString.replace('${p0}', inWord), onEnd});
    }
  } else {
    speech.speak({textToSpeak: outString, onEnd: onEnd});
  }
}